import get from 'lodash/get';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Config, ImmutableTree, Utils } from '@react-awesome-query-builder/core';
import { CustomKeyAttributes } from '@loyalty/shared-common/profile-api-client';
import { EventName, EventSource, Feature } from '@loyalty/shared-common';
import { Typography } from '@mui/material';
import { useAlerts } from '@qualifio/ui-commons';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  BaseConfigSettings,
  configureFieldsForBurningRules,
} from '../rules/components/query-builder/config/fields';
import { BurningRuleFormValue } from './form';
import { Loader } from '../shared/layout/Loader';
import { PeriodicityLimitationForm } from '../rules/components/periodicity-limitation-form';
import { RulesQueryBuilder, initialConfig } from '../rules/components';
import { useApi } from '../shared/api-client/ApiClientProvider';
import { useCurrencies } from '../currency/CurrenciesContext';
import { useCustomKeys } from '../custom-keys/CustomKeysContext';
import { useFeatures } from '../features/FeaturesContext';
import { useProgram } from '../loyalty/ProgramContext';

const { checkTree, loadFromJsonLogic } = Utils;

function makeTree(jsonLogic: any, conf: Config): ImmutableTree {
  const defaultValue = { and: [] };
  const tree = loadFromJsonLogic(jsonLogic || defaultValue, conf);
  if (!tree) {
    throw new Error('Could not load tree from jsonLogic');
  }
  return checkTree(tree, conf);
}

function makeConfig(
  settings: BaseConfigSettings,
  readOnly = false,
  customKeys?: CustomKeyAttributes[],
) {
  return {
    ...initialConfig(settings.t, readOnly),
    fields: configureFieldsForBurningRules(settings, customKeys),
  };
}

export function BurningRulePanelCriteria(): React.ReactElement {
  const { t, i18n } = useTranslation();
  const { notify } = useAlerts();
  const { program } = useProgram();
  const { currencies } = useCurrencies();
  const {
    setValue,
    getValues,
    register,
    formState: { errors },
  } = useFormContext<BurningRuleFormValue>();
  const { gamificationApi } = useApi();
  const { features } = useFeatures();
  const { write: burningRuleWrite } = features[Feature.BURNING_RULE];

  const [readyToLoadQB, setReadyToLoadQB] = useState(false);

  const criteriaFieldName = 'criteria';
  const settings = useMemo<BaseConfigSettings>(
    () => ({
      eventSource: EventSource.QUALIFIO_LOYALTY,
      eventName: EventName.BURNING,
      locale: i18n.language,
      t,
    }),
    [i18n.language],
  );
  const { customKeys } = useCustomKeys();
  const [config, setConfig] = useState<Config>(makeConfig(settings, !burningRuleWrite, customKeys));

  const jsonLogic = get(getValues(), criteriaFieldName);
  const [tree, setTree] = useState<ImmutableTree | undefined>();

  const handleQueryBuilderChange = useCallback(
    (immutableTree: ImmutableTree, conf: Config, newValue: Record<string, any>) => {
      setTree(immutableTree);
      setConfig(conf);
      setValue(criteriaFieldName, newValue, { shouldDirty: true });
    },
    [setValue],
  );

  useEffect(() => {
    gamificationApi
      .getLevels(program.id)
      .then(levels => {
        const newConfig = makeConfig(
          { ...settings, levels, currencies },
          !burningRuleWrite,
          customKeys,
        );
        setConfig(newConfig);
        setTree(makeTree(jsonLogic, newConfig));
      })
      .catch(error => notify(t('burning-rules.errors.failed-fetch-levels', { error }), 'error'))
      .finally(() => setReadyToLoadQB(true));
  }, [program.id, notify, settings]);

  useEffect(() => {
    register(criteriaFieldName);
  }, [register]);

  return !readyToLoadQB || !tree ? (
    <Loader />
  ) : (
    <>
      <RulesQueryBuilder
        // errors[criteriaFieldName]?.message is not considered string|undefined because of typing issue
        error={
          errors[criteriaFieldName]?.message ? `${errors[criteriaFieldName]?.message}` : undefined
        }
        tree={tree}
        onChange={handleQueryBuilderChange}
        config={config}
      />
      <div>
        <Typography variant="h3">{t('earning-rules.properties.options')}</Typography>

        <PeriodicityLimitationForm disabled={!burningRuleWrite} />
      </div>
    </>
  );
}
